import React, { useCallback, useMemo } from 'react';

import {
  Box,
  Button,
  Flex,
  PopoverContent,
  type PopoverContentProps,
  type RadioProps,
  Spacer,
  Text,
  VStack,
  chakra,
  useRadio,
  useRadioGroup,
} from '@chakra-ui/react';
import * as DateFns from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import i18n from '@/lib/i18n/client';
import { AVAILABLE_LANGUAGES } from '@/lib/i18n/constants';
import { TimeOnly } from '@/lib/time';

const TIME_FORMAT = 'HH:mm';

interface TimePickerProps {
  value?: TimeOnly | null;
  onChange: (time: TimeOnly | null) => void;
  onSubmit?: (time: TimeOnly) => void;
  start?: TimeOnly;
  end?: TimeOnly;
  interval?: number;
  locale?: Locale;
  isLoading?: boolean;
}

export type PopoverTimePickerProps = Omit<
  PopoverContentProps,
  keyof TimePickerProps
> &
  TimePickerProps;

export const PopoverTimePicker: React.FC<PopoverTimePickerProps> = ({
  value,
  onChange,
  onSubmit,
  start = new TimeOnly(8),
  end = new TimeOnly(24),
  interval = 30,
  isLoading,

  locale = i18n.language
    ? AVAILABLE_LANGUAGES.find((lang) => lang.key === i18n.language)
        ?.dataFnsLocale
    : enUS,
  ...props
}) => {
  const { t } = useTranslation();

  const timeSlots = useMemo(() => {
    return generateTimeSlots(start, end, interval);
  }, [start, end, interval]);

  const handleChange = useCallback(
    (value: string) => {
      const dateTime = DateFns.parse(value, TIME_FORMAT, new Date());
      const time = new TimeOnly(dateTime.getHours(), dateTime.getMinutes());

      onChange?.(time);
    },
    [onChange]
  );

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'time-picker',
    value: value?.toString(TIME_FORMAT),
    onChange: handleChange,
  });

  const group = getRootProps();

  return (
    <PopoverContent shadow="md" borderRadius="xl" {...props}>
      <VStack spacing={1} align="stretch" p={3}>
        <Text fontSize="md">{t('timeAndDate.time')}</Text>
        <Flex
          height="200px"
          border="solid"
          borderColor="gray.100"
          rounded="lg"
          overflow="auto"
        >
          <Flex
            direction="column"
            flex={1}
            __css={{
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
            {...group}
          >
            {timeSlots.map((value) => {
              const radio = getRadioProps({ value });
              const time = DateFns.parse(value, TIME_FORMAT, new Date());
              const formattedTime = DateFns.format(time, TIME_FORMAT, {
                locale,
              });

              return (
                <RadioCard key={value} {...radio}>
                  {formattedTime}
                </RadioCard>
              );
            })}
          </Flex>
        </Flex>
        <Spacer />
        <Button
          isDisabled={!value}
          isLoading={isLoading}
          variant="@primary"
          size="sm"
          w="full"
          mt="2"
          onClick={() => onSubmit?.(value!)}
        >
          {t('actions.done')}
        </Button>
      </VStack>
    </PopoverContent>
  );
};

const generateTimeSlots = (
  start: TimeOnly,
  end: TimeOnly,
  interval: number
) => {
  const { setMinutes, setHours, startOfToday, endOfToday, format, addMinutes } =
    DateFns;

  const timeSlots = [];
  const startTime = setMinutes(
    setHours(startOfToday(), start.getHours()),
    start.getMinutes()
  );

  let endTime = setMinutes(
    setHours(startOfToday(), end.getHours()),
    end.getMinutes()
  );

  // Midnight (00:00) is a special case
  if (end.getHours() === 0 && end.getMinutes() === 0) {
    endTime = setMinutes(setHours(endOfToday(), 24), 0);
  }

  let currentTime = startTime;

  while (currentTime <= endTime) {
    timeSlots.push(format(currentTime, TIME_FORMAT));
    currentTime = addMinutes(currentTime, interval);
  }

  return timeSlots;
};

function RadioCard(props: RadioProps) {
  const { getInputProps, getRadioProps, getRootProps } = useRadio(props);

  const input = getInputProps();
  const checkbox = getRadioProps();
  const root = getRootProps();

  return (
    <Box w="full" as="label" {...root}>
      <chakra.input {...input} display="none" />
      <Box
        {...checkbox}
        cursor="pointer"
        _checked={{
          bg: 'brand.600',
          color: 'white',
          borderColor: 'teal.600',
        }}
        _focus={{
          boxShadow: 'outline',
        }}
        px={5}
        py={2}
      >
        {props.children}
      </Box>
    </Box>
  );
}
